import React, { useEffect, useState } from "react";
import {
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CampaignIcon from "@mui/icons-material/Campaign";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";
import APIHelper from "../utils/APIHelper";
import { Link as RouterLink, useNavigate } from "react-router-dom";

export default function CampaignsPage({ token }) {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeProducts, setActiveProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  // Form state
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    campaignName: "",
    influencerName: "",
    productId: "",
    commissionRate: 10,
    discountPercentage: 10,
    notes: ""
  });

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await APIHelper.listCampaigns(token, {
        q: query,
        page: page + 1,
        limit: rowsPerPage,
        sortBy,
        sortDir,
      });
      setCampaigns(res.items || []);
      setTotal(res.total || 0);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      setErrorMessage("Failed to fetch campaigns");
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveProducts = async () => {
    setProductsLoading(true);
    try {
      const res = await APIHelper.listProducts(token, {
        status: "Active",
        limit: 100
      });
      setActiveProducts(res.items || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchCampaigns();
    fetchActiveProducts();
  }, [token, page, rowsPerPage, sortBy, sortDir, query]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
    setPage(0);
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Campaign link copied to clipboard!");
  };

  const handleCreateOrUpdate = async () => {
    try {
      if (editId) {
        await APIHelper.updateCampaign(token, editId, formData);
        toast.success("Campaign updated successfully");
      } else {
        await APIHelper.createCampaign(token, formData);
        toast.success("Campaign created successfully");
      }
      setShowForm(false);
      setEditId(null);
      setFormData({
        campaignName: "",
        influencerName: "",
        productId: "",
        commissionRate: 10,
        discountPercentage: 10,
        notes: ""
      });
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to save campaign");
    }
  };

  const openEdit = (campaign) => {
    setEditId(campaign._id);
    setFormData({
      campaignName: campaign.campaignName,
      influencerName: campaign.influencerName,
      productId: campaign.productId?._id || campaign.productId,
      commissionRate: campaign.commissionRate,
      discountPercentage: campaign.discountPercentage,
      notes: campaign.notes || ""
    });
    setShowForm(true);
  };

  if (showForm) {
    return (
      <Box>
        <Box className="mb-4">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              setShowForm(false);
              setEditId(null);
            }}
            className="text-gray-700 dark:text-gray-300 normal-case"
          >
            Back to Campaigns
          </Button>
        </Box>
        
        <Box className="card-modern p-6">
          <Typography variant="h5" className="font-bold text-gray-900 dark:text-white mb-6">
            {editId ? "Edit Creator Campaign" : "Create Creator Campaign"}
          </Typography>

          <Box className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <TextField
              label="Campaign Name"
              fullWidth
              value={formData.campaignName}
              onChange={(e) => setFormData({ ...formData, campaignName: e.target.value })}
              placeholder="e.g. Summer Collection Launch"
            />
            <TextField
              label="Influencer Name"
              fullWidth
              value={formData.influencerName}
              onChange={(e) => setFormData({ ...formData, influencerName: e.target.value })}
              placeholder="e.g. @cool_creator"
            />
            
            <FormControl fullWidth disabled={!!editId}>
              <InputLabel>Product</InputLabel>
              <Select
                value={formData.productId}
                label="Product"
                onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
              >
                {activeProducts.map((p) => (
                  <MenuItem key={p._id} value={p._id}>
                    {p.title} ({p.sku})
                  </MenuItem>
                ))}
              </Select>
              {activeProducts.length === 0 && !productsLoading && (
                <Typography variant="caption" color="error" className="mt-1">
                  No active products found. <RouterLink to="/dashboard/products" className="underline">Create one first.</RouterLink>
                </Typography>
              )}
            </FormControl>

            <Box className="grid grid-cols-2 gap-4">
              <TextField
                label="Commission (%)"
                type="number"
                fullWidth
                value={formData.commissionRate}
                onChange={(e) => setFormData({ ...formData, commissionRate: parseFloat(e.target.value) })}
              />
              <TextField
                label="Discount (%)"
                type="number"
                fullWidth
                value={formData.discountPercentage}
                onChange={(e) => setFormData({ ...formData, discountPercentage: parseFloat(e.target.value) })}
              />
            </Box>

            <Box className="md:col-span-2">
              <TextField
                label="Internal Notes"
                multiline
                rows={3}
                fullWidth
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Reference notes for you..."
              />
            </Box>
          </Box>

          <Box className="mt-8 flex justify-end gap-4">
            <Button 
                onClick={() => setShowForm(false)} 
                className="text-gray-600 dark:text-gray-400 normal-case"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateOrUpdate}
              disabled={!formData.campaignName || !formData.influencerName || !formData.productId}
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-2.5 rounded-xl normal-case"
            >
              {editId ? "Update Campaign" : "Create Campaign"}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="flex flex-col">
      <Box className="flex items-center justify-between mb-6">
        <Box className="flex items-center gap-2">
          <CampaignIcon className="text-primary-600 dark:text-primary-400 text-3xl" />
          <Typography variant="h5" className="font-bold text-gray-900 dark:text-white">
            Creator Campaigns
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddCircleIcon />}
          onClick={() => setShowForm(true)}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl shadow-lg normal-case"
        >
          Create Creator Campaign
        </Button>
      </Box>

      {errorMessage && (
        <Alert severity="error" className="mb-6 rounded-xl">
          {errorMessage}
        </Alert>
      )}

      {/* Main Content Area */}
      {loading ? (
        <Box className="flex justify-center py-20">
          <CircularProgress />
        </Box>
      ) : campaigns.length === 0 && !query ? (
        <Box className="card-modern p-12 text-center flex flex-col items-center justify-center">
          {activeProducts.length === 0 ? (
            <>
              <Box className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-4">
                <span className="text-4xl">📦</span>
              </Box>
              <Typography variant="h6" className="font-bold text-gray-900 dark:text-white mb-2">
                Create Products First
              </Typography>
              <Typography className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
                You need to have at least one active product to unlock Creator Campaigns.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/dashboard/products")}
                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-2.5 rounded-xl normal-case"
              >
                Go to Products
              </Button>
            </>
          ) : (
            <>
              <Box className="w-20 h-20 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-4">
                <CampaignIcon className="text-primary-600 dark:text-primary-400 text-4xl" />
              </Box>
              <Typography variant="h6" className="font-bold text-gray-900 dark:text-white mb-2">
                Grow your sales with Influencers
              </Typography>
              <Typography className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
                Partner with creators to promote your products. Create a unique checkout link for them and track everything.
              </Typography>
              <Button
                variant="contained"
                onClick={() => setShowForm(true)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-2.5 rounded-xl normal-case"
              >
                Create Your First Campaign
              </Button>
            </>
          )}
        </Box>
      ) : (
        <Box className="card-modern p-6">
          <Box className="flex items-center gap-4 mb-6">
             <TextField
                size="small"
                placeholder="Search campaigns or influencers..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="max-w-md flex-grow"
              />
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow className="bg-gray-50 dark:bg-gray-800">
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === "campaignName"}
                      direction={sortBy === "campaignName" ? sortDir : "asc"}
                      onClick={() => handleSort("campaignName")}
                      className="font-bold"
                    >
                      Campaign Information
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell align="center">Link</TableCell>
                  <TableCell align="center">Comm %</TableCell>
                  <TableCell align="center">Disc %</TableCell>
                  <TableCell align="center">Orders</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaigns.map((c) => (
                  <TableRow key={c._id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <TableCell>
                      <Box>
                        <Typography className="font-semibold text-gray-900 dark:text-white">
                          {c.campaignName}
                        </Typography>
                        <Typography variant="caption" className="text-gray-500">
                          Influencer: <span className="text-primary-600 font-medium">{c.influencerName}</span>
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{c.productId?.title || "N/A"}</Typography>
                        <Typography variant="caption" className="text-gray-500">{c.productSku}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Copy Campaign checkout link">
                        <IconButton size="small" onClick={() => handleCopyLink(c.checkoutUrl)}>
                          <ContentCopyIcon fontSize="small" className="text-primary-600" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">{c.commissionRate}%</TableCell>
                    <TableCell align="center">
                       <Chip label={`${c.discountPercentage}%`} size="small" color="success" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">{c.stats?.totalOrders || 0}</TableCell>
                    <TableCell align="right" className="font-semibold">
                      ₹{((c.stats?.totalRevenue || 0) / 100).toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(c)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Box>
      )}
    </Box>
  );
}
